<template>
  <div>
    <!-- Button -->
    <button type="button" class="btn btn-sm btn-outline-light" @click="showModal">
      <fa icon="file-excel" fixed-width />
      Import
    </button>
    <!-- Modal -->
    <div ref="modal" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modal" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title">Import Interviews</h5>
            <button type="button" class="close" aria-label="Close" data-dismiss="modal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body py-0">
            <div class="row" v-if="!hasData">
              <div class="col">
                <div class="alert alert-info" role="alert">
                  <h5 class="alert-heading">Instructions</h5>
                  <p>Select an Excel or CSV file using the file upload dialog below.</p>
                  <hr />
                  <p>
                    <span class="span-link" @click="downloadSampleFile"><fa icon="file-excel" fixed-width /> Download our sample file</span> and use it as a
                    template.
                  </p>
                </div>
                <div v-if="isBadFile" class="alert alert-warning" role="alert">
                  <fa icon="exclamation-triangle" fixed-width /> <strong>Warning</strong> This file doesn't match our import format. Please update your file and
                  try again.
                </div>
                <form ref="form" class="p-2" novalidate @submit.prevent>
                  <div class="form-group">
                    <input ref="fileUpload" type="file" accept=".xls,.xlsx,.csv" class="form-control-file" @change.prevent="onFile" />
                  </div>
                </form>
              </div>
            </div>
            <div class="row" v-if="hasData">
              <div class="col">
                <ErrorSummary :error="error" />
                <p>
                  Below is a preview of your data. While you review, we'll check for duplicates. When you're ready, click the
                  <strong>Import</strong> button below to complete the import process.
                </p>
                <div v-if="isValidating" class="alert alert-info" role="alert">
                  <fa icon="sync" fixed-width spin /> <strong>Validating</strong> {{ totalRowsValidated }} of {{ totalRows }} rows ...
                </div>
                <div v-if="hasInvalidRows" class="alert alert-danger" role="alert">
                  <fa icon="exclamation-triangle" fixed-width /> We detected one or more invalid Registration IDs and/or Email Addresses.
                  <hr />
                  These rows must be corrected before they can be imported. <span class="span-link" @click="reset">Click here to upload an updated file.</span>
                </div>
                <div v-if="hasDuplicates" class="alert alert-warning" role="alert">
                  <fa icon="exclamation-triangle" fixed-width /> We detected one or more duplicate registration IDs and/or Email Addresses.
                  <hr />
                  <ul class="list-unstyled">
                    <li>Duplicate Registration IDs (<fa icon="exclamation-triangle" fixed-width />) are accepted.</li>
                    <li>Duplicate Email Addresses (<fa icon="times" fixed-width />) within the same Investigation will be skipped.</li>
                  </ul>
                </div>
                <div class="table-wrap my-2">
                  <table class="table">
                    <thead>
                      <tr>
                        <th scope="col">Validation Status</th>
                        <th scope="col">Registration Id</th>
                        <th scope="col">Email Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="i in fileUploadData" :key="i.key">
                        <td>
                          <fa :icon="getRowStatusIcon(i)" fixed-width :spin="getRowStatusIconSpin(i)" :class="getRowStatusIconClass(i)" />
                          <span class="d-none d-lg-inline">{{ getRowStatusText(i) }}</span>
                        </td>
                        <td :class="getRowClass(i)">{{ i.registrationId || "---" }}</td>
                        <td :class="getRowClass(i)">{{ i.emailAddress || "---" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="alert alert-info">
                  <fa icon="info-circle" fixed-width class="mb-1" /> You may edit these interviews after the import process to change the preferred language and
                  enable extended time before sending them to individuals.
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="submit" :disabled="importButtonDisabled">{{ importButtonText }}</button>
            <button type="button" class="btn btn-primary" @click="closeModal">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!--File Upload-->
  </div>
</template>

<script>
import $ from "jquery";
import XLSX from "xlsx";
import formatter from "@/utils/formatter";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    isLoading: Boolean,
    investigation: Object,
  },
  data() {
    return {
      loadingMessage: null,
      error: null,

      fileSelected: false,
      fileUploadData: [],
    };
  },
  watch: {
    async fileUploadData(newValue, oldValue) {
      if (newValue && newValue.length && oldValue && !oldValue.length) {
        await this.validateFile();
      }
    },
  },
  computed: {
    formatter: () => formatter,
    hasData() {
      return this.fileUploadData && this.fileUploadData.length;
    },
    isBadFile() {
      return this.fileSelected && !this.hasData;
    },
    importButtonDisabled() {
      return !this.hasData || this.loadingMessage || this.hasInvalidRows;
    },
    importButtonText() {
      return this.loadingMessage ? this.loadingMessage : "Import";
    },
    totalRows() {
      return this.fileUploadData.length;
    },
    totalRowsValidated() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.validated === true ? sum + 1 : sum;
      }, 0);
    },
    isValidating() {
      return this.totalRows !== this.totalRowsValidated;
    },
    hasInvalidRows() {
      return !!this.invalidCount;
    },
    invalidCount() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.isInvalidRegistrationId || row.isInvalidEmailAddress ? sum + 1 : sum;
      }, 0);
    },
    hasDuplicates() {
      return !!this.duplicateCount;
    },
    duplicateCount() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.isDuplicateRegistrationId || row.isDuplicateEmailAddress ? sum + 1 : sum;
      }, 0);
    },
  },
  methods: {
    showModal() {
      $(this.$refs.modal).modal("show");
    },
    closeModal() {
      this.reset();
      $(this.$refs.modal).modal("hide");
    },
    reset() {
      this.error = null;
      this.loadingMessage = null;
      this.fileSelected = false;
      this.fileUploadData = [];
    },
    async submit() {
      // payload
      var payloads = this.fileUploadData
        .filter(i => !i.isDuplicateEmailAddress)
        .map(i => ({
          registrationId: i.registrationId,
          emailAddress: i.emailAddress,
          extendedTime: false,
          extendedTimeReason: null,
          languageCode: "en",
        }));

      // api
      this.loadingMessage = "Saving...";
      await payloads
        .reduce(async (promise, payload) => {
          await promise; // wait for previous
          await new Promise(r => setTimeout(r, 250)); // throttle
          await this.$axios
            .post(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews`, payload)
            .catch(err => (this.error = err));
        }, Promise.resolve())
        .then(() => {
          this.closeModal();
          this.reset();
          this.$emit("reload");
        })
        .finally(() => (this.loadingMessage = null));
    },
    downloadSampleFile() {
      var workbook = XLSX.utils.book_new();
      var worksheet = XLSX.utils.aoa_to_sheet([
        ["Registration Id", "Email Address"],
        ["registration-1", "example1@example.com"],
        ["registration-2", "example2@example.com"],
        ["registration-3", "example3@example.com"],
      ]);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Interviews");
      XLSX.writeFile(workbook, "Verensics - Investigation - Interviews - Sample.xlsx");
    },
    async onFile(e) {
      // parse
      this.fileSelected = true;
      var file = e.target.files[0];
      var fileData = await file.arrayBuffer();
      var workbook = XLSX.read(fileData);

      // extract data
      var sheet1 = workbook.SheetNames[0];
      var worksheet = workbook.Sheets[sheet1];
      var worksheetData = XLSX.utils.sheet_to_json(worksheet);
      // collect data
      this.fileUploadData = [];
      var key = 0;
      worksheetData.forEach(r => {
        var registrationId = r["Registration Id"];
        var emailAddress = r["Email Address"];
        if (registrationId || emailAddress) {
          this.fileUploadData.push({
            key: key++,
            registrationId: registrationId,
            emailAddress: emailAddress,
            validated: false,
          });
        }
      });
    },
    async validateFile() {
      if (!this.fileUploadData || !this.fileUploadData.length) return;
      this.loadingMessage = "Validating...";

      // Create a map to track email occurrences
      const emailCounts = new Map();
      this.fileUploadData.forEach(row => {
        if (row.emailAddress) {
          const email = row.emailAddress.toLowerCase();
          emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
        }
      });

      await this.fileUploadData.reduce(async (promise, row) => {
        await promise; // wait for previous
        await new Promise(r => setTimeout(r, 20)); // throttle
        // Mark as duplicate if email appears more than once in the file
        if (row.emailAddress && emailCounts.get(row.emailAddress.toLowerCase()) > 1) {
          row.isDuplicateEmailAddress = true;
        }
        await this.validateRow(row);
      }, Promise.resolve());
      this.loadingMessage = null;
    },
    async validateRow(row) {
      row.isInvalidRegistrationId = this.isInvalidRegistrationId(row.registrationId);
      row.isInvalidEmailAddress = this.isInvalidEmailAddress(row.emailAddress);
      row.isDuplicateRegistrationId = await this.isDuplicateRegistrationId(row.registrationId);
      // Only check for duplicate email if not already marked as duplicate
      if (!row.isDuplicateEmailAddress) {
        row.isDuplicateEmailAddress = await this.isDuplicateEmailAddress(row.emailAddress);
      }
      // force binding by updating the reference being enumerated
      this.fileUploadData = this.fileUploadData.slice();
      row.validated = true;
    },
    isInvalidRegistrationId(registrationId) {
      return !registrationId || !registrationId.toString().trim();
    },
    isInvalidEmailAddress(emailAddress) {
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email
      var regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      return !emailAddress || !emailAddress.trim() || !regex.test(emailAddress);
    },
    async isDuplicateRegistrationId(registrationId) {
      let encodedValue = encodeURIComponent(registrationId);
      return await this.$axios.get(`/v1/organizations/${this.investigation.organizationId}/reports/${encodedValue}/duplicate`).then(res => {
        var isDuplicate = res.data.count > 0 && !!res.data.previousDate;
        return isDuplicate;
      });
    },
    async isDuplicateEmailAddress(emailAddress) {
      let encodedValue = encodeURIComponent(emailAddress);
      return await this.$axios
        .get(
          `/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews/email-addresses/${encodedValue}/duplicate`
        )
        .then(res => {
          var isDuplicate = res.data.isDuplicate === true;
          return isDuplicate;
        });
    },
    getRowClass(row) {
      if (row.isDuplicateEmailAddress) return "text-muted strike-through";
      return "";
    },
    getRowStatusIcon(row) {
      if (!row.validated) return "sync";
      if (row.isInvalidRegistrationId || row.isInvalidEmailAddress) return "times";
      if (row.isDuplicateRegistrationId) return "exclamation-triangle";
      if (row.isDuplicateEmailAddress) return "exclamation-triangle";
      return "check";
    },
    getRowStatusIconClass(row) {
      if (!row.validated) return "";
      if (row.isInvalidRegistrationId || row.isInvalidEmailAddress) return "text-danger";
      if (row.isDuplicateRegistrationId || row.isDuplicateEmailAddress) return "text-warning";
      return "text-success";
    },
    getRowStatusIconSpin(row) {
      return !row.validated;
    },
    getRowStatusText(row) {
      if (row.isInvalidRegistrationId) return "Invalid Registration Id";
      if (row.isInvalidEmailAddress) return "Invalid Email Address";
      if (row.isDuplicateRegistrationId) return "Duplicate Registration Id";
      if (row.isDuplicateEmailAddress) return "Duplicate Email Address";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.span-link {
  font-weight: bold;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
}

.table-wrap {
  display: block;
  max-height: 500px;
  overflow: hidden;
  overflow-y: scroll;
}

.strike-through {
  text-decoration: line-through;
}
</style>
